// Cache service for n8n data with localStorage fallback

const CACHE_PREFIX = 'n8n_cache_';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

export class DataCache {
  // Save data to cache
  static set<T>(key: string, data: T, expiryMs: number = CACHE_EXPIRY): void {
    if (typeof window === 'undefined') return;
    
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiry: expiryMs,
    };
    
    try {
      localStorage.setItem(`${CACHE_PREFIX}${key}`, JSON.stringify(entry));
    } catch (error) {
      console.error('Failed to cache data:', error);
    }
  }

  // Get data from cache
  static get<T>(key: string): T | null {
    if (typeof window === 'undefined') return null;
    
    try {
      const cached = localStorage.getItem(`${CACHE_PREFIX}${key}`);
      if (!cached) return null;
      
      const entry: CacheEntry<T> = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - entry.timestamp > entry.expiry) {
        localStorage.removeItem(`${CACHE_PREFIX}${key}`);
        return null;
      }
      
      return entry.data;
    } catch (error) {
      console.error('Failed to read cache:', error);
      return null;
    }
  }

  // Check if cache exists and is valid
  static has(key: string): boolean {
    return this.get(key) !== null;
  }

  // Clear specific cache
  static clear(key: string): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(`${CACHE_PREFIX}${key}`);
  }

  // Clear all caches
  static clearAll(): void {
    if (typeof window === 'undefined') return;
    Object.keys(localStorage)
      .filter(key => key.startsWith(CACHE_PREFIX))
      .forEach(key => localStorage.removeItem(key));
  }
}

